import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlexBetween from "components/FlexBetween";
import WidgetWrapper from "components/WidgetWrapper";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import Dropzone from "react-dropzone";
import MUITiptapWrapper from "components/TiptapWrapper";
import { imageUpload } from "reduxStore/actions/uploadUtils";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useInstructionalCoachingGuide } from "./hooks/useInstructionalCoachingGuide";

function InstructionalCoachingGuide() {
  const {
    isLargeScreen,
    isSystemAdmin,
    openGuideEdit,
    handleOpenGuideEdit,
    handleCloseGuideEdit,
    handleSaveGuide,
    handleDeleteGuide,
    addTextBlock,
    addImageBlock,
    updateBlockData,
    removeBlock,
    contentBlocks,
    sanitizeContent,
    isLoadingGuides,
    defaultImageURL,
    guideId,
    swapPublicToXxLarge,
  } = useInstructionalCoachingGuide();

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Generate array of image URLs
  const imageUrls = contentBlocks
    .filter((block) => block.type === "image")
    .map((block) => swapPublicToXxLarge(block.data));

  // Click handler to open the lightbox
  const openLightbox = (index) => {
    const imageBlockIndexes = contentBlocks.reduce((acc, block, i) => {
      if (block.type === "image") acc.push(i);
      return acc;
    }, []);

    const actualIndex = imageBlockIndexes.indexOf(index);
    if (actualIndex >= 0) {
      setPhotoIndex(actualIndex);
      setLightboxOpen(true);
    }
  };

  if (isLoadingGuides) {
    return <Typography>Loading guides...</Typography>;
  }

  // Updated onDragEnd using @hello-pangea/dnd and stable IDs
  const onDragEnd = (result) => {
    if (!result.destination) return;

    // Reorder the contentBlocks array
    const reordered = Array.from(contentBlocks);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Replace all blocks with the new order
    updateBlockData(-1, reordered);
  };

  return (
    <Box
      m={isLargeScreen ? "0 0 0 0.5rem" : "1.5rem 0.5rem 2rem 0.5rem"}
      height="100%"
      minHeight="100vh"
    >
      <WidgetWrapper>
        <FlexBetween>
          {isSystemAdmin && (
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: "5px",
                fontSize: "12px",
                padding: "5px 10px",
              }}
              onClick={handleOpenGuideEdit}
            >
              <DriveFileRenameOutlineOutlinedIcon sx={{ mr: "0.5rem" }} />
              Edit Guide
            </Button>
          )}
        </FlexBetween>

        <Box mt={2}>
          {contentBlocks.length > 0 ? (
            contentBlocks.map((block, index) => (
              <Box key={block.id} marginY={2}>
                {block.type === "text" ? (
                  <Box
                    sx={{
                      maxWidth: "900px",
                      borderRadius: "8px",
                      margin: "0 auto",
                      padding: "1rem",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                      // backgroundColor: "white",
                    }}
                    dangerouslySetInnerHTML={sanitizeContent(block.data)}
                  />
                ) : (
                  <Box
                    sx={{
                      maxWidth: "900px",
                      margin: "0 auto",
                    }}
                  >
                    <img
                      src={swapPublicToXxLarge(block.data)}
                      alt="ALT Coach"
                      loading="lazy"
                      style={{
                        width: "100%",
                        maxHeight: "600px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        display: "block",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                        cursor: "pointer",
                      }}
                      onClick={() => openLightbox(index)}
                    />
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Typography variant="body1">No content available.</Typography>
          )}
        </Box>
      </WidgetWrapper>

      <Dialog
        open={openGuideEdit}
        onClose={handleCloseGuideEdit}
        fullWidth
        maxWidth="md"
      >
        <Box p={2}>
          <DialogTitle variant="h5" gutterBottom>
            Edit ALT Coach Guide
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              This section allows you to edit the ALT Coach
              guide.
            </Typography>
            <Typography variant="body1" paragraph>
              You can add, remove, or edit the content as needed. Once you are
              done, click the <strong>Create Guide</strong> button to update the
              guide.
            </Typography>

            <Box my={3} sx={{ overflow: "visible" }}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="contentBlocks">
                  {(provided) => (
                    <Box {...provided.droppableProps} ref={provided.innerRef}>
                      {contentBlocks.map((block, index) => (
                        <Draggable
                          key={block.id}
                          draggableId={block.id}
                          index={index}
                        >
                          {(provided) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              marginY={2}
                              position="relative"
                            >
                              <Box
                                padding={2}
                                borderRadius="8px"
                                boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
                                bgcolor="background.paper"
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                                style={{
                                  backgroundColor: "white",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  padding: "8px",
                                }}
                              >
                                <IconButton
                                  aria-label="Remove block"
                                  onClick={() => removeBlock(index)}
                                  style={{
                                    position: "absolute",
                                    right: 0,
                                    top: 0,
                                    zIndex: 1,
                                  }}
                                >
                                  <CloseIcon
                                    sx={{
                                      color: "#333",
                                      opacity: 0.4,
                                    }}
                                  />
                                </IconButton>
                                <Box {...provided.dragHandleProps}>
                                  <DragIndicatorIcon
                                    fontSize="small"
                                    sx={{
                                      cursor: "grab",
                                      opacity: 0.4,
                                      color: "#333",
                                    }}
                                  />
                                </Box>
                              </Box>

                              {block.type === "text" ? (
                                <MUITiptapWrapper
                                  value={block.data}
                                  onChange={(content) =>
                                    updateBlockData(index, content)
                                  }
                                  placeholder="Enter text here..."
                                />
                              ) : (
                                <Dropzone
                                  onDrop={async (acceptedFiles) => {
                                    const file = acceptedFiles[0];
                                    const publicImageUrl = await imageUpload(
                                      file
                                    );
                                    if (publicImageUrl) {
                                      updateBlockData(index, publicImageUrl);
                                    } else {
                                      console.error("Image upload failed");
                                    }
                                  }}
                                >
                                  {({ getRootProps, getInputProps }) => (
                                    <Box
                                      {...getRootProps()}
                                      border="1px dashed"
                                      textAlign="center"
                                      height={200}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="center"
                                      cursor="pointer"
                                      borderRadius="4px"
                                      style={{
                                        backgroundImage: `url(${
                                          block.data || defaultImageURL
                                        })`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                      }}
                                    >
                                      <input {...getInputProps()} />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          backgroundColor:
                                            "rgba(255,255,255,0.9)",
                                          padding: "8px",
                                          borderRadius: "4px",
                                          boxShadow:
                                            "0 2px 8px rgba(0, 0, 0, 0.1)",
                                          cursor: "pointer",
                                          "&:hover": {
                                            backgroundColor:
                                              "rgba(255,255,255,0.8)",
                                          },
                                          fontSize: "0.9rem",
                                          fontWeight: "500",
                                          color: "#333",
                                        }}
                                      >
                                        Drop image here, or click to select
                                        image
                                      </Typography>
                                    </Box>
                                  )}
                                </Dropzone>
                              )}
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>

            <FlexBetween>
              <Box>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={addTextBlock}
                  sx={{
                    marginRight: "1rem",
                  }}
                >
                  Add Text Block
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={addImageBlock}
                >
                  Add Image Block
                </Button>
              </Box>
              <Box>
                {guideId && (
                  <Button
                    variant="contained"
                    color="error"
                    onClick={handleDeleteGuide}
                    sx={{
                      marginRight: "1rem",
                    }}
                  >
                    Delete Guide
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveGuide}
                >
                  {guideId ? "Update Guide" : "Create Guide"}
                </Button>
              </Box>
            </FlexBetween>
          </DialogContent>
        </Box>
      </Dialog>

      {lightboxOpen && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={photoIndex}
          slides={imageUrls.map((src) => ({ src }))}
          plugins={[Thumbnails]}
          on={{
            view: ({ index }) => setPhotoIndex(index),
          }}
        />
      )}
    </Box>
  );
}

export default InstructionalCoachingGuide;
